import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  FilterAccordionHeader,
  Button,
  Card,
  KeyValue,
  Icon,
  Row,
  Col,
  Select,
  Checkbox,
  MultiColumnList,
} from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { useField } from 'react-final-form';
import { Pluggable, useStripes, useOkapiKy, CalloutContext } from '@folio/stripes/core';

/**
 * Shared permissions component used in both report and dashboard forms.
 *
 * Props:
 * - visibility: current visibility value from the form ('public' | 'private' | restrictedValue)
 * - restrictedValue: the value that means "restricted/shared" — defaults to 'restricted' (reports),
 *   pass 'shared' for dashboards
 * - accordionId: unique id for the accordion element
 */
function AuthorizedUsersSection({
  visibility,
  restrictedValue,
  accordionId,
}) {
  const [filterToggle, setFilterToggle] = useState(true);
  const [isCurrentUserOwner, setIsCurrentUserOwner] = useState(false);
  const [usersMap, setUsersMap] = useState({});
  const intl = useIntl();
  const stripes = useStripes();
  const okapiKy = useOkapiKy();
  const callout = useContext(CalloutContext);
  const currentUser = stripes.user.user;

  const { input: { value: viewUsers,    onChange: setViewUsers    } } = useField('viewUsers',    { defaultValue: [] });
  const { input: { value: editUsers,    onChange: setEditUsers    } } = useField('editUsers',    { defaultValue: [] });
  const { input: { value: executeUsers, onChange: setExecuteUsers } } = useField('executeUsers', { defaultValue: [] });
  const { input: { value: ownerId, onChange: setOwnerId }, meta: { error: ownerError } } = useField('owner', { defaultValue: '' });

  const isPublic        = visibility === 'public';
  const isPrivate       = visibility === 'private';
  const isRestricted    = visibility === restrictedValue;
  const isOwnerRequired = isPrivate || isRestricted;

  // Fetch display names for all user IDs
  useEffect(() => {
    const allIds = [...(viewUsers || []), ...(editUsers || []), ...(executeUsers || []), ...(ownerId ? [ownerId] : [])]
      .filter(id => id && !usersMap[id]);
    if (!allIds.length) return;
    const cql = [...new Set(allIds)].map(id => `id=="${id}"`).join(' or ');
    okapiKy.get(`users?query=${encodeURIComponent(cql)}&limit=100`).json()
      .then(data => {
        const map = {};
        (data.users || []).forEach(u => { map[u.id] = u; });
        setUsersMap(prev => ({ ...prev, ...map }));
      })
      .catch(() => {});
  }, [viewUsers, editUsers, executeUsers, ownerId]);

  // Clear permissions when visibility changes to public
  useEffect(() => {
    if (isPublic) {
      setViewUsers([]);
      setEditUsers([]);
      setExecuteUsers([]);
      setOwnerId('');
    }
  }, [isPublic]);

  // Auto-check "I am the owner" if current user is the owner
  useEffect(() => {
    if (ownerId && ownerId === currentUser.id) setIsCurrentUserOwner(true);
  }, [ownerId, currentUser.id]);

  const getUserDisplayName = (userId) => {
    const user = usersMap[userId];
    if (!user) return userId;
    const { firstName, lastName } = user.personal || {};
    if (firstName && lastName) return `${firstName} ${lastName}`;
    return user.username || userId;
  };

  const handleCurrentUserAsOwner = (e) => {
    const checked = e.target.checked;
    setIsCurrentUserOwner(checked);
    setOwnerId(checked ? currentUser.id : '');
    if (checked) {
      setUsersMap(prev => ({
        ...prev,
        [currentUser.id]: {
          ...currentUser,
          personal: currentUser.personal || {
            firstName: currentUser.firstName || '',
            lastName: currentUser.lastName || '',
          },
        },
      }));
    }
  };

  const addUser = (user) => {
    const allIds = [...(viewUsers || []), ...(editUsers || []), ...(executeUsers || [])];
    if (allIds.includes(user.id)) return;
    if (user.id === ownerId) {
      callout?.sendCallout({
        type: 'warning',
        message: intl.formatMessage({
          id: 'ui-reports.permissions.ownerAlreadyHasAccess',
          defaultMessage: 'The owner already has full access.',
        }),
      });
      return;
    }
    setUsersMap(prev => ({ ...prev, [user.id]: user }));
    setViewUsers([...(viewUsers || []), user.id]);
  };

  const removeUser = (userId, type) => {
    if (type === 'view')    setViewUsers((viewUsers    || []).filter(id => id !== userId));
    if (type === 'edit')    setEditUsers((editUsers    || []).filter(id => id !== userId));
    if (type === 'execute') setExecuteUsers((executeUsers || []).filter(id => id !== userId));
  };

  const changePermission = (userId, oldType, newType) => {
    removeUser(userId, oldType);
    if (newType === 'view')    setViewUsers([...(viewUsers    || []).filter(id => id !== userId), userId]);
    if (newType === 'edit')    setEditUsers([...(editUsers    || []).filter(id => id !== userId), userId]);
    if (newType === 'execute') setExecuteUsers([...(executeUsers || []).filter(id => id !== userId), userId]);
  };

  const permissionOptions = [
    { value: 'view',    label: intl.formatMessage({ id: 'ui-reports.permissions.view',    defaultMessage: 'View' }) },
    { value: 'edit',    label: intl.formatMessage({ id: 'ui-reports.permissions.edit',    defaultMessage: 'Edit' }) },
    { value: 'execute', label: intl.formatMessage({ id: 'ui-reports.permissions.execute', defaultMessage: 'Execute' }) },
  ];

  const allUsersRows = [
    ...(viewUsers    || []).map(id => ({ id, type: 'view' })),
    ...(editUsers    || []).map(id => ({ id, type: 'edit' })),
    ...(executeUsers || []).map(id => ({ id, type: 'execute' })),
  ];

  const visibilityStyles = {
    public:  { bg: '#e8f5e9', border: '#4caf50', icon: 'unlock', id: 'ui-reports.permissions.publicInfo' },
    private: { bg: '#fff3e0', border: '#ff9800', icon: 'lock',   id: 'ui-reports.permissions.privateInfo' },
    [restrictedValue]: { bg: '#e3f2fd', border: '#2196f3', icon: 'users', id: 'ui-reports.permissions.restrictedInfo' },
  };

  const visStyle = visibilityStyles[visibility] || visibilityStyles.public;

  return (
    <Accordion
      id={accordionId}
      label={<FormattedMessage id="ui-reports.newReport.saveReportPane.permissions.accordion.header" defaultMessage="User Permissions" />}
      onToggle={() => setFilterToggle(!filterToggle)}
      open={filterToggle}
      separator
      header={FilterAccordionHeader}
    >
      {/* Visibility info banner */}
      <div style={{ padding: '12px', backgroundColor: visStyle.bg, border: `1px solid ${visStyle.border}`, borderRadius: '4px', marginBottom: '12px' }}>
        <Icon icon={visStyle.icon} size="medium" />
        <span style={{ marginInlineStart: '8px' }}>
          <FormattedMessage id={visStyle.id} defaultMessage={visibility} />
        </span>
      </div>

      {/* Owner section */}
      {isOwnerRequired && (
        <>
          <Row>
            <Col xs={12}>
              <Checkbox
                id={`${accordionId}-current-user-owner-checkbox`}
                checked={isCurrentUserOwner}
                onChange={handleCurrentUserAsOwner}
                label={
                  <strong>
                    <FormattedMessage id="ui-reports.permissions.iAmTheOwner" defaultMessage="I am the owner" />
                  </strong>
                }
              />
            </Col>
          </Row>

          {ownerId && (
            <Card style={{ marginBottom: '12px', padding: '12px', backgroundColor: '#f0f0f0' }}>
              <Row>
                <Col xs={10}>
                  <KeyValue
                    label={<FormattedMessage id="ui-reports.permissions.owner" defaultMessage="Owner" />}
                    value={getUserDisplayName(ownerId)}
                  />
                </Col>
                <Col xs={2} style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                  <Button buttonStyle="none" onClick={() => { setOwnerId(''); setIsCurrentUserOwner(false); }}>
                    <Icon icon="trash" />
                  </Button>
                </Col>
              </Row>
            </Card>
          )}

          <div style={{ marginBottom: '12px' }}>
            <Pluggable
              type="find-user"
              selectUser={(user) => {
                setUsersMap(prev => ({ ...prev, [user.id]: user }));
                setOwnerId(user.id);
                setIsCurrentUserOwner(user.id === currentUser.id);
              }}
              renderTrigger={(triggerProps) => (
                <Button {...triggerProps} buttonStyle="primary" disabled={isCurrentUserOwner} fullWidth>
                  <Icon icon="plus-sign" />
                  {' '}
                  {ownerId
                    ? <FormattedMessage id="ui-reports.permissions.changeOwner" defaultMessage="Change Owner" />
                    : <FormattedMessage id="ui-reports.permissions.setOwner" defaultMessage="Set Owner" />
                  }
                  {!ownerId && <span style={{ color: 'red' }}> *</span>}
                </Button>
              )}
            />
            {ownerError && (
              <div style={{ color: 'red', marginTop: '4px', fontSize: '0.85rem' }}>
                {ownerError}
              </div>
            )}
          </div>
        </>
      )}

      {/* Authorized users — restricted/shared only */}
      {isRestricted && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', marginTop: '16px' }}>
            <strong>
              <FormattedMessage id="ui-reports.permissions.authorizedUsers" defaultMessage="Authorized Users" />
            </strong>
            <Pluggable
              type="find-user"
              selectUser={addUser}
              renderTrigger={(triggerProps) => (
                <Button {...triggerProps} buttonStyle="default">
                  <Icon icon="plus-sign" />
                  {' '}
                  <FormattedMessage id="ui-reports.newReport.saveReportPane.permissions.addUser" defaultMessage="Add User" />
                </Button>
              )}
            />
          </div>

          {allUsersRows.length > 0 ? (
            <MultiColumnList
              id={`${accordionId}-authorized-users-list`}
              interactive={false}
              visibleColumns={['name', 'permission', 'action']}
              contentData={allUsersRows.map(u => ({
                name: getUserDisplayName(u.id),
                permission: (
                  <Select
                    marginBottom0
                    value={u.type}
                    dataOptions={permissionOptions}
                    onChange={(e) => changePermission(u.id, u.type, e.target.value)}
                  />
                ),
                action: (
                  <Button buttonStyle="none" marginBottom0 onClick={() => removeUser(u.id, u.type)}>
                    <Icon icon="trash" />
                  </Button>
                ),
              }))}
              columnMapping={{
                name:       <FormattedMessage id="ui-reports.permissions.userName"  defaultMessage="User" />,
                permission: <FormattedMessage id="ui-reports.permissions.type"      defaultMessage="Permission" />,
                action:     '',
              }}
              columnWidths={{ name: '55%', permission: '35%', action: '10%' }}
            />
          ) : (
            <div style={{ padding: '12px', textAlign: 'center', color: '#666', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
              <FormattedMessage id="ui-reports.newReport.saveReportPane.permissions.noUsers" defaultMessage="No authorized users added yet." />
            </div>
          )}
        </>
      )}
    </Accordion>
  );
}

AuthorizedUsersSection.propTypes = {
  visibility: PropTypes.string.isRequired,
  restrictedValue: PropTypes.string,
  accordionId: PropTypes.string,
};

AuthorizedUsersSection.defaultProps = {
  restrictedValue: 'restricted',
  accordionId: 'permissions-accordion',
};

export default AuthorizedUsersSection;
